import { ChangeDetectorRef, Component, OnInit } from '@angular/core';

import { LoadingService } from './core/services/loading.service';
import { CoreService } from './core/services/core.service';
import { SidebarProfile } from './models/common/sidebar';
import { BaseSessionService } from './core/services/base-session.service';
import { loginRouteForPortal, portalForRole } from './core/auth/portal-routes';
import { TokenRefreshService } from './core/services/token-refresh.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  /** Drives the single app-wide loading overlay. */
  readonly loading$;

  /** Profile to display in the header (set by portal components). */
  headerProfile: SidebarProfile | null = null;

  /** Whether to show logout button in the header. */
  showHeaderLogout = false;

  constructor(
    private readonly loadingService: LoadingService,
    private readonly coreService: CoreService,
    private readonly session: BaseSessionService,
    private readonly cdr: ChangeDetectorRef,
    private readonly tokenRefresh: TokenRefreshService
  ) {
    this.loading$ = this.loadingService.loading$;
  }

  ngOnInit(): void {
    // The session survives a page reload; the refresh timer does not.
    this.tokenRefresh.scheduleRefresh();

    // Portal shells publish their header state from ngOnInit, which runs while this
    // component is already being checked. Applying it in a microtask lands the value
    // after the current pass (avoids NG0100) without setTimeout's frame of lag.
    this.coreService.headerProfile$.subscribe((profile) => {
      this.applyAfterCheck(() => (this.headerProfile = profile));
    });

    this.coreService.showHeaderLogout$.subscribe((show) => {
      this.applyAfterCheck(() => (this.showHeaderLogout = show));
    });
  }

  private loggingOut = false;

  async onLogout(): Promise<void> {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;

    // Capture the portal before clearing the session so we return to the right login.
    const loginRoute = loginRouteForPortal(portalForRole(this.session.getCurrentUser()?.role));
    // Reloads rather than router.navigate — see logoutAndReload for why.
    await this.session.logoutAndReload(loginRoute, 'You have logged out successfully!');
  }

  /** Defers a binding write past the in-flight change-detection pass. */
  private applyAfterCheck(update: () => void): void {
    queueMicrotask(() => {
      update();
      this.cdr.markForCheck();
    });
  }
}
